import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { bankDetailsAPI, companiesAPI, customersAPI, itemsAPI } from '@/lib/utils/api';
import { ArrowRight, BanknoteIcon, Building, Package, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState({
    companies: 0,
    customers: 0,
    items: 0,
    bankAccounts: 0,
    loading: true,
  });

  const modules = [
    {
      title: 'Companies',
      description: 'Manage your company information and logo',
      icon: <Building className="h-5 w-5" />,
      path: '/companies',
      color: 'bg-blue-500/10 text-blue-600',
      borderColor: 'border-blue-200',
    },
    {
      title: 'Customers',
      description: 'Manage your customer database',
      icon: <Users className="h-5 w-5" />,
      path: '/customers',
      color: 'bg-green-500/10 text-green-600',
      borderColor: 'border-green-200',
    },
    {
      title: 'Items',
      description: 'Manage products and services',
      icon: <Package className="h-5 w-5" />,
      path: '/items',
      color: 'bg-purple-500/10 text-purple-600',
      borderColor: 'border-purple-200',
    },
    {
      title: 'Bank Details',
      description: 'Manage banking information',
      icon: <BanknoteIcon className="h-5 w-5" />,
      path: '/bank-details',
      color: 'bg-orange-500/10 text-orange-600',
      borderColor: 'border-orange-200',
    },
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [companiesRes, customersRes, itemsRes, bankDetailsRes] = await Promise.all([
        companiesAPI.getAll(),
        customersAPI.getAll(),
        itemsAPI.getAll(),
        bankDetailsAPI.getAll(),
      ]);

      setStats({
        companies: Array.isArray(companiesRes.data)
          ? companiesRes.data.length
          : companiesRes.data.companies?.length || 0,
        customers: Array.isArray(customersRes.data)
          ? customersRes.data.length
          : customersRes.data.customers?.length || 0,
        items: Array.isArray(itemsRes.data)
          ? itemsRes.data.length
          : itemsRes.data.items?.length || 0,
        bankAccounts: Array.isArray(bankDetailsRes.data)
          ? bankDetailsRes.data.length
          : bankDetailsRes.data.bankDetails?.length || 0,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  const StatCard = ({
    title,
    value,
    icon,
    color,
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
  }) => (
    <Card className="overflow-hidden shadow-md border-none">
      <CardContent className="px-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">{title}</p>
            <h3 className="text-2xl font-bold">{value}</h3>
          </div>
          <div className={`p-3 rounded-full ${color}`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Welcome to TRK-Invoicia</h1>
            <p className="text-muted-foreground mt-2">
              Manage your business operations efficiently from your dashboard
            </p>
          </div>
          {/* <Badge variant="outline" className="gap-1">
            <BarChart3 className="h-3 w-3" />
            Analytics
          </Badge> */}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Companies"
          value={stats.loading ? '...' : stats.companies}
          icon={<Building className="h-5 w-5" />}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          title="Customers"
          value={stats.loading ? '...' : stats.customers}
          icon={<Users className="h-5 w-5" />}
          color="bg-green-100 text-green-600"
        />
        <StatCard
          title="Items"
          value={stats.loading ? '...' : stats.items}
          icon={<Package className="h-5 w-5" />}
          color="bg-purple-100 text-purple-600"
        />
        <StatCard
          title="Bank Accounts"
          value={stats.loading ? '...' : stats.bankAccounts}
          icon={<BanknoteIcon className="h-5 w-5" />}
          color="bg-orange-100 text-orange-600"
        />
      </div>

      {/* Module Cards */}
      <Card className="shadow-md border-none">
        <CardHeader>
          <CardTitle>Business Modules</CardTitle>
          <CardDescription>
            Choose a module below to manage different aspects of your business
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {modules.map((module) => (
              <Link key={module.path} to={module.path} className="group">
                <Card
                  className={`h-full border ${module.borderColor} hover:border-primary/50 transition-all duration-200 hover:shadow-md group-hover:translate-y-[-2px]`}
                >
                  <CardContent className="px-6">
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div
                        className={`p-3 rounded-lg ${module.color} ${module.color.includes('text-') ? '' : 'text-primary'}`}
                      >
                        {module.icon}
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold text-lg">{module.title}</h3>
                        <p className="text-sm text-muted-foreground">{module.description}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Manage
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="border-none">
        <CardHeader>
          <CardTitle>Quick Overview</CardTitle>
          <CardDescription>Summary of your business data at a glance</CardDescription>
        </CardHeader>
        <CardContent>
          {stats.loading ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="px-6">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-8 w-3/4" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-none bg-gradient-to-br from-blue-50 to-blue-100/50">
                <CardContent className="px-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{stats.companies}</div>
                    <div className="text-sm font-medium text-blue-700 mt-2">Companies</div>
                    <div className="text-xs text-blue-600/70 mt-1">Active entities</div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none bg-gradient-to-br from-green-50 to-green-100/50">
                <CardContent className="px-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{stats.customers}</div>
                    <div className="text-sm font-medium text-green-700 mt-2">Customers</div>
                    <div className="text-xs text-green-600/70 mt-1">In database</div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none bg-gradient-to-br from-purple-50 to-purple-100/50">
                <CardContent className="px-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">{stats.items}</div>
                    <div className="text-sm font-medium text-purple-700 mt-2">Items</div>
                    <div className="text-xs text-purple-600/70 mt-1">Products & Services</div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none bg-gradient-to-br from-orange-50 to-orange-100/50">
                <CardContent className="px-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-600">{stats.bankAccounts}</div>
                    <div className="text-sm font-medium text-orange-700 mt-2">Bank Accounts</div>
                    <div className="text-xs text-orange-600/70 mt-1">Configured</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Welcome Card with Tips */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 shadow-md">
        <CardContent>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Ready to create your first invoice?</h3>
              <p className="text-sm text-muted-foreground">
                Make sure all your company details, customers, and items are set up before creating
                invoices.
              </p>
            </div>
            <Link to={'/invoices/new'}>
              <Button size="lg" className="gap-2">
                Get Started
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
